/**
 * Progreso en vivo del alta (T049 · FR-011).
 * EventSource — prohibido sondear con fetch en useEffect.
 */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { useSesion } from '../../auth'

interface PasoVista {
    orden: number
    nombre: string
    estado: string
    detalle: string | null
    error: string | null
}

const ESTILO: Record<string, string> = {
    pendiente: 'text-amber-300',
    hecho: 'text-emerald-400',
    fallido: 'text-red-400',
    compensado: 'text-orange-300',
}

export function AltaProgreso() {
    const { data: sesion } = useSesion()
    const { id } = useParams()
    const num = Number(id)
    const [estado, setEstado] = useState<string>('…')
    const [pasos, setPasos] = useState<Map<number, PasoVista>>(new Map())
    const [fin, setFin] = useState<string | null>(null)
    const [reconectando, setReconectando] = useState(false)

    useEffect(() => {
        if (!Number.isFinite(num) || num <= 0) return
        if (sesion && sesion.role !== 'admin') return

        const es = new EventSource(`/api/altas/${num}/stream`, { withCredentials: true })
        es.onopen = () => setReconectando(false)
        es.onerror = () => setReconectando(true)

        es.addEventListener('estado', (ev) => {
            const data = JSON.parse((ev as MessageEvent).data as string) as { estado: string }
            setEstado(data.estado)
        })
        es.addEventListener('paso', (ev) => {
            const p = JSON.parse((ev as MessageEvent).data as string) as PasoVista
            setPasos((prev) => {
                const next = new Map(prev)
                next.set(p.orden, p)
                return next
            })
        })
        es.addEventListener('fin', (ev) => {
            const data = JSON.parse((ev as MessageEvent).data as string) as { estado: string }
            setFin(data.estado)
            setEstado(data.estado)
            es.close()
        })

        return () => es.close()
    }, [num, sesion])

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    const lista = [...pasos.values()].sort((a, b) => a.orden - b.orden)

    return (
        <div className="mx-auto max-w-xl space-y-4" data-testid="alta-progreso">
            <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                ← Operación
            </Link>
            <h1 className="text-lg font-semibold text-neutral-100">Alta #{num}</h1>
            <p className="text-sm">
                Estado:{' '}
                <span data-testid="progreso-estado" className="font-medium text-neutral-100">
                    {estado}
                </span>
                {reconectando && !fin ? (
                    <span className="ml-2 text-xs text-amber-400" data-testid="progreso-reconectando">
                        reconectando…
                    </span>
                ) : null}
            </p>
            <ol className="space-y-2" data-testid="progreso-pasos">
                {lista.length === 0 ? (
                    <li className="text-sm text-neutral-500">Esperando el primer paso…</li>
                ) : (
                    lista.map((p) => (
                        <li
                            key={p.orden}
                            className="flex gap-3 text-sm"
                            data-testid={`paso-${p.orden}`}
                            data-estado={p.estado}
                        >
                            <span className="w-6 tabular-nums text-neutral-500">{p.orden}</span>
                            <span className="flex-1 text-neutral-200">{p.nombre}</span>
                            <span className={ESTILO[p.estado] ?? 'text-neutral-400'}>{p.estado}</span>
                        </li>
                    ))
                )}
            </ol>
            {fin ? (
                <p className="text-sm text-emerald-400" data-testid="progreso-fin">
                    Terminado: {fin}
                </p>
            ) : null}
        </div>
    )
}
